import { useRef } from "react";
import { m as motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

export interface JourneyStep {
  n: string;
  title: string;
  body: string;
  time: string;
  image: string;
}

interface Props {
  steps: JourneyStep[];
}

/** Vertical scroll-driven timeline with progress line and parallax images. */
export function ProcessJourney({ steps }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 80%", "end 20%"],
  });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <div ref={ref} className="relative">
      {/* Progress rail */}
      <div className="absolute left-6 top-0 bottom-0 w-px bg-white/10 md:left-1/2 md:-translate-x-px" aria-hidden="true">
        <motion.div
          style={{ scaleY: reduce ? 1 : lineScale, transformOrigin: "top" }}
          className="absolute inset-0 bg-gradient-to-b from-mint via-mint to-mint/40"
        />
      </div>

      <ol className="space-y-20 md:space-y-32">
        {steps.map((s, i) => {
          const flipped = i % 2 === 1;
          return (
            <li key={s.n} className="relative grid grid-cols-[3rem_1fr] gap-4 md:grid-cols-2 md:gap-12">
              {/* Dot */}
              <div className="absolute left-6 top-2 z-10 -translate-x-1/2 md:left-1/2">
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="h-4 w-4 rounded-full border-2 border-mint bg-background shadow-[0_0_0_6px_rgba(0,0,0,0.9),0_0_24px_4px_color-mix(in_oklab,var(--mint)_55%,transparent)]"
                />
              </div>

              {/* Text column */}
              <div
                className={
                  "col-start-2 md:row-start-1 " +
                  (flipped ? "md:col-start-2 md:pl-16" : "md:col-start-1 md:pr-16 md:text-right")
                }
              >
                <motion.div
                  initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  className={flipped ? "" : "md:ml-auto md:max-w-md"}
                >
                  <div className="font-serif text-7xl font-semibold text-mint md:text-8xl">{s.n}</div>
                  <h3 className="mt-2 text-2xl font-semibold md:text-3xl">{s.title}</h3>
                  <p className="mt-3 max-w-md text-base text-muted-foreground">{s.body}</p>
                  <div className="mt-4 text-[11px] uppercase tracking-[0.18em] text-mint/80">{s.time}</div>
                </motion.div>
              </div>

              {/* Image column */}
              <div
                className={
                  "hidden md:row-start-1 md:block " +
                  (flipped ? "md:col-start-1 md:pr-16" : "md:col-start-2 md:pl-16")
                }
              >
                <ParallaxImage src={s.image} alt={`${s.title} — steg ${s.n} i Fennys filmproduktionsprocess`} />
              </div>

              {/* Mobile image */}
              <div className="col-start-2 md:hidden">
                <div className="mt-4 overflow-hidden rounded-2xl border border-white/10">
                  <img src={s.image} alt={`${s.title} — steg ${s.n} i Fennys filmproduktionsprocess`} className="aspect-[16/10] w-full object-cover" loading="lazy" />
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ParallaxImage({ src, alt }: { src: string; alt: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["-8%", "8%"]);

  return (
    <div ref={ref} className="relative overflow-hidden rounded-2xl border border-white/10">
      <motion.img
        style={{ y }}
        src={src}
        alt={alt}
        loading="lazy"
        className="aspect-[16/10] w-full scale-110 object-cover"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-background/40 via-transparent to-mint/10" />
    </div>
  );
}
